import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { classifyApiError } from '@shared/api/api-client';
import { messagingApi } from '@shared/api/messaging';
import { getCallDeviceId } from '../lib/deviceId';
import type {
  CallCenterCallSort,
  StartCustomCallRequest,
} from '@jema-manager/shared/types/messaging.types';

const DEFAULT_ACTIVE_CALLS_REFETCH_INTERVAL_MS = 5_000;
const MIN_ACTIVE_CALLS_REFETCH_INTERVAL_MS = 2_500;
const MAX_ACTIVE_CALLS_REFETCH_INTERVAL_MS = 30_000;

export type CallLogFilters = {
  direction?: 'inbound' | 'outbound';
  status?: string;
  limit?: number;
  cursor?: string;
  startDate?: string;
  endDate?: string;
  sort?: CallCenterCallSort;
};

export type VoicemailFilters = {
  status?: string;
  limit?: number;
  cursor?: string;
  startDate?: string;
  endDate?: string;
  sort?: CallCenterCallSort;
};

type ActiveCallsQueryOptions = {
  enabled?: boolean;
  refetchInterval?: number | false;
  refetchIntervalInBackground?: boolean;
};

type ContactSearchOptions = {
  allowEmpty?: boolean;
  enabled?: boolean;
  limit?: number;
  type?: 'patient' | 'doctor' | 'staff' | 'all';
};

export const callsQueryKeys = {
  all: ['calls'] as const,
  logsRoot: () => [...callsQueryKeys.all, 'logs'] as const,
  logs: (patientId?: string | null, filters: CallLogFilters = {}) =>
    [
      ...callsQueryKeys.logsRoot(),
      patientId ?? 'global',
      filters.direction ?? 'all-directions',
      filters.status ?? 'all-statuses',
      filters.limit ?? 50,
      filters.cursor ?? 'first-page',
      filters.startDate ?? 'all-start',
      filters.endDate ?? 'all-end',
      filters.sort ?? 'newest',
    ] as const,
  voicemailsRoot: () => [...callsQueryKeys.all, 'voicemails'] as const,
  voicemails: (patientId?: string | null, filters: VoicemailFilters = {}) =>
    [
      ...callsQueryKeys.voicemailsRoot(),
      patientId ?? 'global',
      filters.status ?? 'all-statuses',
      filters.limit ?? 50,
      filters.cursor ?? 'first-page',
      filters.startDate ?? 'all-start',
      filters.endDate ?? 'all-end',
      filters.sort ?? 'newest',
    ] as const,
  voicemailPlayback: (voicemailId: string) =>
    [...callsQueryKeys.voicemailsRoot(), voicemailId, 'playback'] as const,
  recordings: (callId: string) => [...callsQueryKeys.all, 'recordings', callId] as const,
  recordingPlayback: (recordingId: string) =>
    [...callsQueryKeys.all, 'recordings', recordingId, 'playback'] as const,
  qualitySummary: (callId: string) => [...callsQueryKeys.all, 'quality', callId] as const,
  active: () => [...callsQueryKeys.all, 'active'] as const,
  handlingSettings: () => [...callsQueryKeys.all, 'handlingSettings'] as const,
  availability: () => [...callsQueryKeys.all, 'availability'] as const,
  contacts: (query: string, limit?: number, type?: ContactSearchOptions['type']) =>
    [...callsQueryKeys.all, 'contacts', query.trim(), limit ?? 10, type ?? 'all'] as const,
  webrtcCredential: () => [...callsQueryKeys.all, 'webrtcCredential'] as const,
  managedWebRtcCredential: (staffUserId: string) =>
    [...callsQueryKeys.all, 'managedWebRtcCredential', staffUserId] as const,
  managedZoiperCredential: (staffUserId: string) =>
    [...callsQueryKeys.all, 'managedZoiperCredential', staffUserId] as const,
  managedSipEndpoints: (staffUserId: string) =>
    [...callsQueryKeys.all, 'managedSipEndpoints', staffUserId] as const,
};

const normalizeActiveCallsRefetchInterval = (
  refetchInterval: ActiveCallsQueryOptions['refetchInterval']
) => {
  if (refetchInterval === false) return false;
  const interval = refetchInterval ?? DEFAULT_ACTIVE_CALLS_REFETCH_INTERVAL_MS;
  return Math.min(
    Math.max(interval, MIN_ACTIVE_CALLS_REFETCH_INTERVAL_MS),
    MAX_ACTIVE_CALLS_REFETCH_INTERVAL_MS
  );
};

const RATE_LIMITED_ACTIVE_CALLS_REFETCH_INTERVAL_MS = 60_000;
const HIDDEN_TAB_ACTIVE_CALLS_REFETCH_INTERVAL_MS = 20_000;

/**
 * Resolves the active-calls poll interval per tick. The server is the source of
 * truth via Telnyx webhooks, so polling exists only to surface changes; it must
 * never be the thing that exhausts the API budget:
 * - while the last fetch was rate-limited, back off hard (60s) instead of
 *   polling straight back into the 429 wall;
 * - hidden tabs poll slowly (20s) — enough to keep ringing detection alive
 *   without every background tab burning quota at the foreground cadence.
 */
export const resolveActiveCallsRefetchInterval = (
  requested: ActiveCallsQueryOptions['refetchInterval'],
  lastError: unknown
): number | false => {
  const normalized = normalizeActiveCallsRefetchInterval(requested);
  if (normalized === false) return false;
  if (classifyApiError(lastError) === 'rate_limit') {
    return RATE_LIMITED_ACTIVE_CALLS_REFETCH_INTERVAL_MS;
  }
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return Math.max(normalized, HIDDEN_TAB_ACTIVE_CALLS_REFETCH_INTERVAL_MS);
  }
  return normalized;
};

const invalidateCallLists = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({ queryKey: callsQueryKeys.active() });
  void queryClient.invalidateQueries({ queryKey: callsQueryKeys.logsRoot() });
};

export const useCallLogs = (patientId?: string | null, filters: CallLogFilters = {}) =>
  useQuery({
    queryKey: callsQueryKeys.logs(patientId, filters),
    queryFn: () =>
      messagingApi.listCallCenterCalls({
        patientId: patientId ?? undefined,
        limit: filters.limit ?? 50,
        ...(filters.direction ? { direction: filters.direction } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.cursor ? { cursor: filters.cursor } : {}),
        ...(filters.startDate ? { startDate: filters.startDate } : {}),
        ...(filters.endDate ? { endDate: filters.endDate } : {}),
        ...(filters.sort ? { sort: filters.sort } : {}),
      }),
  });

export const useCallCenterVoicemails = (
  patientId?: string | null,
  filters: VoicemailFilters = {}
) =>
  useQuery({
    queryKey: callsQueryKeys.voicemails(patientId, filters),
    queryFn: () =>
      messagingApi.listCallCenterVoicemails({
        patientId: patientId ?? undefined,
        limit: filters.limit ?? 50,
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.cursor ? { cursor: filters.cursor } : {}),
        ...(filters.startDate ? { startDate: filters.startDate } : {}),
        ...(filters.endDate ? { endDate: filters.endDate } : {}),
        ...(filters.sort ? { sort: filters.sort } : {}),
      }),
  });

export const useCallCenterVoicemailPlaybackUrl = (voicemailId: string | null, enabled = false) =>
  useQuery({
    queryKey: voicemailId
      ? callsQueryKeys.voicemailPlayback(voicemailId)
      : [...callsQueryKeys.voicemailsRoot(), 'none', 'playback'],
    queryFn: () => messagingApi.getCallCenterVoicemailPlaybackUrl(voicemailId as string),
    enabled: Boolean(voicemailId) && enabled,
  });

export const useCallRecordings = (callId: string) =>
  useQuery({
    queryKey: callsQueryKeys.recordings(callId),
    queryFn: () => messagingApi.listCallRecordings(callId),
    enabled: Boolean(callId),
  });

export const useCallRecordingPlaybackUrl = (recordingId: string | null, enabled = true) =>
  useQuery({
    queryKey: recordingId
      ? callsQueryKeys.recordingPlayback(recordingId)
      : [...callsQueryKeys.all, 'recordings', 'none', 'playback'],
    queryFn: () => messagingApi.getCallRecordingPlaybackUrl(recordingId as string),
    enabled: Boolean(recordingId) && enabled,
  });

export const useMarkCallCenterVoicemailRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (voicemailId: string) => messagingApi.markCallCenterVoicemailRead(voicemailId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: callsQueryKeys.voicemailsRoot() });
    },
  });
};

export const useMarkCallCenterVoicemailUnread = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (voicemailId: string) => messagingApi.markCallCenterVoicemailUnread(voicemailId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: callsQueryKeys.voicemailsRoot() });
    },
  });
};

export const useCreateCallCenterDirectRoom = () =>
  useMutation({
    mutationFn: (input: Parameters<typeof messagingApi.createDirectRoom>[0]) =>
      messagingApi.createDirectRoom(input),
  });

export const useActiveCalls = (options: ActiveCallsQueryOptions = {}) =>
  useQuery({
    queryKey: callsQueryKeys.active(),
    queryFn: () => messagingApi.listActiveCallCenterCalls(),
    enabled: options.enabled ?? true,
    refetchInterval: query =>
      resolveActiveCallsRefetchInterval(options.refetchInterval, query.state.error),
    refetchIntervalInBackground: options.refetchIntervalInBackground ?? false,
  });

export const useCallCenterHandlingSettings = (enabled = true) =>
  useQuery({
    queryKey: callsQueryKeys.handlingSettings(),
    queryFn: () => messagingApi.getCallCenterHandlingSettings(),
    enabled,
  });

export const useUpdateCallCenterHandlingSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof messagingApi.updateCallCenterHandlingSettings>[0]) =>
      messagingApi.updateCallCenterHandlingSettings(input),
    onSuccess: response => {
      queryClient.setQueryData(callsQueryKeys.handlingSettings(), response);
      void queryClient.invalidateQueries({ queryKey: callsQueryKeys.handlingSettings() });
    },
  });
};

export const useCallCenterStaffAvailability = (enabled = true) =>
  useQuery({
    queryKey: callsQueryKeys.availability(),
    queryFn: () => messagingApi.getCallCenterStaffAvailability(),
    enabled,
  });

export const useUpdateCallCenterStaffAvailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof messagingApi.updateCallCenterStaffAvailability>[0]) =>
      messagingApi.updateCallCenterStaffAvailability(input),
    onSuccess: response => {
      queryClient.setQueryData(callsQueryKeys.availability(), response);
      void queryClient.invalidateQueries({ queryKey: callsQueryKeys.availability() });
      void queryClient.invalidateQueries({ queryKey: callsQueryKeys.active() });
    },
  });
};

export const useCallCenterContacts = (query: string, options: ContactSearchOptions = {}) => {
  const trimmedQuery = query.trim();
  const limit = options.limit ?? 10;
  const allowEmpty = options.allowEmpty === true;

  return useQuery({
    queryKey: callsQueryKeys.contacts(trimmedQuery, limit, options.type),
    queryFn: () =>
      messagingApi.searchCallCenterContacts({
        query: trimmedQuery,
        limit,
        ...(options.type ? { type: options.type } : {}),
      }),
    enabled: (Boolean(trimmedQuery) || allowEmpty) && (options.enabled ?? true),
  });
};

export const useStartCustomCall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StartCustomCallRequest) => messagingApi.startCustomCall(input),
    onSuccess: () => {
      invalidateCallLists(queryClient);
    },
  });
};

export const useCreateClickToDialIntent = () =>
  useMutation({
    mutationFn: (input: Parameters<typeof messagingApi.createClickToDialIntent>[0]) =>
      messagingApi.createClickToDialIntent(input),
  });

export const useStartClickToDialBrowserFallback = () =>
  useMutation({
    mutationFn: (intentId: string) => messagingApi.startClickToDialBrowserFallback(intentId),
  });

export const useCancelClickToDialIntent = () =>
  useMutation({
    mutationFn: (intentId: string) => messagingApi.cancelClickToDialIntent(intentId),
  });

export const useWebRtcCredential = () =>
  useQuery({
    queryKey: callsQueryKeys.webrtcCredential(),
    queryFn: () => messagingApi.getWebRtcCredential(),
  });

export const useUpdateWebRtcCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof messagingApi.updateWebRtcCredential>[0]) =>
      messagingApi.updateWebRtcCredential(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: callsQueryKeys.webrtcCredential() });
    },
  });
};

export const useDeleteWebRtcCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => messagingApi.deleteWebRtcCredential(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: callsQueryKeys.webrtcCredential() });
    },
  });
};

export const useManagedWebRtcCredential = (staffUserId: string | null, enabled = true) =>
  useQuery({
    queryKey: staffUserId
      ? callsQueryKeys.managedWebRtcCredential(staffUserId)
      : [...callsQueryKeys.all, 'managedWebRtcCredential', 'none'],
    queryFn: () => messagingApi.getManagedWebRtcCredential(staffUserId as string),
    enabled: Boolean(staffUserId) && enabled,
  });

export const useUpdateManagedWebRtcCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      staffUserId,
      input,
    }: {
      staffUserId: string;
      input: Parameters<typeof messagingApi.updateManagedWebRtcCredential>[1];
    }) => messagingApi.updateManagedWebRtcCredential(staffUserId, input),
    onSuccess: (response, variables) => {
      queryClient.setQueryData(
        callsQueryKeys.managedWebRtcCredential(variables.staffUserId),
        response
      );
      void queryClient.invalidateQueries({
        queryKey: callsQueryKeys.managedWebRtcCredential(variables.staffUserId),
      });
      void queryClient.invalidateQueries({ queryKey: callsQueryKeys.webrtcCredential() });
    },
  });
};

export const useProvisionManagedWebRtcCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      staffUserId,
      input,
    }: {
      staffUserId: string;
      input: Parameters<typeof messagingApi.provisionManagedWebRtcCredential>[1];
    }) => messagingApi.provisionManagedWebRtcCredential(staffUserId, input),
    onSuccess: (response, variables) => {
      queryClient.setQueryData(
        callsQueryKeys.managedWebRtcCredential(variables.staffUserId),
        response
      );
      void queryClient.invalidateQueries({
        queryKey: callsQueryKeys.managedWebRtcCredential(variables.staffUserId),
      });
      void queryClient.invalidateQueries({ queryKey: callsQueryKeys.webrtcCredential() });
    },
  });
};

export const useDeleteManagedWebRtcCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staffUserId: string) => messagingApi.deleteManagedWebRtcCredential(staffUserId),
    onSuccess: (response, staffUserId) => {
      queryClient.setQueryData(callsQueryKeys.managedWebRtcCredential(staffUserId), response);
      void queryClient.invalidateQueries({
        queryKey: callsQueryKeys.managedWebRtcCredential(staffUserId),
      });
      void queryClient.invalidateQueries({ queryKey: callsQueryKeys.webrtcCredential() });
    },
  });
};

export const useManagedZoiperCredential = (staffUserId: string | null, enabled = true) =>
  useQuery({
    queryKey: staffUserId
      ? callsQueryKeys.managedZoiperCredential(staffUserId)
      : [...callsQueryKeys.all, 'managedZoiperCredential', 'none'],
    queryFn: () => messagingApi.getManagedZoiperCredential(staffUserId as string),
    enabled: Boolean(staffUserId) && enabled,
  });

export const useManagedSipEndpoints = (staffUserId: string | null, enabled = true) =>
  useQuery({
    queryKey: staffUserId
      ? callsQueryKeys.managedSipEndpoints(staffUserId)
      : [...callsQueryKeys.all, 'managedSipEndpoints', 'none'],
    queryFn: () => messagingApi.listManagedSipEndpoints(staffUserId as string),
    enabled: Boolean(staffUserId) && enabled,
  });

export const useCallingSetupStatus = (enabled = true) =>
  useQuery({
    queryKey: [...callsQueryKeys.all, 'setupStatus'],
    queryFn: messagingApi.getCallingSetupStatus,
    enabled,
  });

const useSetupMutation = <T = void>(mutationFn: (input: T) => Promise<unknown>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [...callsQueryKeys.all, 'setupStatus'] }),
  });
};

export const useStartFallbackNumberVerification = () =>
  useSetupMutation(messagingApi.startFallbackNumberVerification);
export const useConfirmFallbackNumberVerification = () =>
  useSetupMutation(messagingApi.confirmFallbackNumberVerification);
export const useResendFallbackNumberVerification = () =>
  useSetupMutation(() => messagingApi.resendFallbackNumberVerification());
export const useUpdateFallbackNumber = () => useSetupMutation(messagingApi.updateFallbackNumber);
export const useRemoveFallbackNumber = () =>
  useSetupMutation(() => messagingApi.removeFallbackNumber());

export const useProvisionManagedSipEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      staffUserId,
      input,
    }: {
      staffUserId: string;
      input: Parameters<typeof messagingApi.provisionManagedSipEndpoint>[1];
    }) => messagingApi.provisionManagedSipEndpoint(staffUserId, input),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: callsQueryKeys.managedSipEndpoints(variables.staffUserId),
      });
      queryClient.invalidateQueries({ queryKey: [...callsQueryKeys.all, 'setupStatus'] });
    },
  });
};

export const useUpdateManagedSipEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      staffUserId,
      endpointId,
      input,
    }: {
      staffUserId: string;
      endpointId: string;
      input: Parameters<typeof messagingApi.updateManagedSipEndpoint>[2];
    }) => messagingApi.updateManagedSipEndpoint(staffUserId, endpointId, input),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: callsQueryKeys.managedSipEndpoints(variables.staffUserId),
      });
      queryClient.invalidateQueries({ queryKey: [...callsQueryKeys.all, 'setupStatus'] });
    },
  });
};

export const useRefreshManagedSipEndpointRegistration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ staffUserId, endpointId }: { staffUserId: string; endpointId: string }) =>
      messagingApi.refreshManagedSipEndpointRegistration(staffUserId, endpointId),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: callsQueryKeys.managedSipEndpoints(variables.staffUserId),
      });
      queryClient.invalidateQueries({ queryKey: [...callsQueryKeys.all, 'setupStatus'] });
    },
  });
};

export const useRevokeManagedSipEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ staffUserId, endpointId }: { staffUserId: string; endpointId: string }) =>
      messagingApi.revokeManagedSipEndpoint(staffUserId, endpointId),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: callsQueryKeys.managedSipEndpoints(variables.staffUserId),
      });
      queryClient.invalidateQueries({ queryKey: [...callsQueryKeys.all, 'setupStatus'] });
    },
  });
};

export const useProvisionManagedZoiperCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      staffUserId,
      input,
    }: {
      staffUserId: string;
      input: Parameters<typeof messagingApi.provisionManagedZoiperCredential>[1];
    }) => messagingApi.provisionManagedZoiperCredential(staffUserId, input),
    onSuccess: (response, variables) => {
      queryClient.setQueryData(callsQueryKeys.managedZoiperCredential(variables.staffUserId), {
        credential: response.credential,
      });
    },
  });
};

export const useRevokeManagedZoiperCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staffUserId: string) => messagingApi.revokeManagedZoiperCredential(staffUserId),
    onSuccess: (response, staffUserId) => {
      queryClient.setQueryData(callsQueryKeys.managedZoiperCredential(staffUserId), response);
    },
  });
};

export const useRecordWebRtcCall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof messagingApi.recordWebRtcCall>[0]) =>
      messagingApi.recordWebRtcCall(input),
    onSuccess: () => {
      invalidateCallLists(queryClient);
    },
  });
};

export const useSyncWebRtcCallProviderIds = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof messagingApi.syncWebRtcCallProviderIds>[0]) =>
      messagingApi.syncWebRtcCallProviderIds(input),
    onSuccess: () => {
      invalidateCallLists(queryClient);
    },
  });
};

export const usePrepareWebRtcCall = () =>
  useMutation({
    mutationFn: (input: Parameters<typeof messagingApi.prepareWebRtcCall>[0]) =>
      messagingApi.prepareWebRtcCall(input),
  });

export const useClaimIncomingCall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (callLogId: string) =>
      messagingApi.claimIncomingCall(callLogId, {
        acceptedFrom: 'manager_web',
        deviceId: getCallDeviceId(),
      }),
    onSuccess: () => {
      invalidateCallLists(queryClient);
    },
  });
};

export const useHoldCallCenterCall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (callLogId: string) => messagingApi.holdCallCenterCall(callLogId),
    onSuccess: () => {
      invalidateCallLists(queryClient);
    },
  });
};

export const useResumeCallCenterCall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (callLogId: string) => messagingApi.resumeCallCenterCall(callLogId),
    onSuccess: () => {
      invalidateCallLists(queryClient);
    },
  });
};

export const useControlCallRecording = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      callLogId,
      input,
    }: {
      callLogId: string;
      input: Parameters<typeof messagingApi.controlCallRecording>[1];
    }) => messagingApi.controlCallRecording(callLogId, input),
    onSuccess: (_response, variables) => {
      invalidateCallLists(queryClient);
      void queryClient.invalidateQueries({
        queryKey: callsQueryKeys.qualitySummary(variables.callLogId),
      });
    },
  });
};

export const useControlCallTranscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      callLogId,
      input,
    }: {
      callLogId: string;
      input: Parameters<typeof messagingApi.controlCallTranscription>[1];
    }) => messagingApi.controlCallTranscription(callLogId, input),
    onSuccess: (_response, variables) => {
      invalidateCallLists(queryClient);
      void queryClient.invalidateQueries({
        queryKey: callsQueryKeys.qualitySummary(variables.callLogId),
      });
    },
  });
};

export const useControlCallSupervisor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      callLogId,
      input,
    }: {
      callLogId: string;
      input: Parameters<typeof messagingApi.controlCallSupervisor>[1];
    }) => messagingApi.controlCallSupervisor(callLogId, input),
    onSuccess: (_response, variables) => {
      invalidateCallLists(queryClient);
      void queryClient.invalidateQueries({
        queryKey: callsQueryKeys.qualitySummary(variables.callLogId),
      });
    },
  });
};

export const useRecordCallDebugEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      callLogId,
      input,
    }: {
      callLogId: string;
      input: Parameters<typeof messagingApi.recordCallDebugEvent>[1];
    }) => messagingApi.recordCallDebugEvent(callLogId, input),
    onSuccess: (_response, variables) => {
      void queryClient.invalidateQueries({
        queryKey: callsQueryKeys.qualitySummary(variables.callLogId),
      });
    },
  });
};

export const useCallQualitySummary = (callLogId: string | null, enabled = true) =>
  useQuery({
    queryKey: callLogId
      ? callsQueryKeys.qualitySummary(callLogId)
      : [...callsQueryKeys.all, 'quality', 'none'],
    queryFn: () => messagingApi.getCallQualitySummary(callLogId as string),
    enabled: Boolean(callLogId) && enabled,
    refetchInterval: enabled ? 15_000 : false,
  });

export const useCallCenterDeviceHeartbeat = () =>
  useMutation({
    mutationFn: (input: Parameters<typeof messagingApi.heartbeatCallCenterDevice>[0]) =>
      messagingApi.heartbeatCallCenterDevice(input),
  });

export const useTransferCallCenterCall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      callLogId,
      input,
    }: {
      callLogId: string;
      input: Parameters<typeof messagingApi.transferCallCenterCall>[1];
    }) => messagingApi.transferCallCenterCall(callLogId, input),
    onSuccess: () => {
      invalidateCallLists(queryClient);
    },
  });
};

export const useAddCallCenterConferenceParticipant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      callLogId,
      input,
    }: {
      callLogId: string;
      input: Parameters<typeof messagingApi.addCallCenterConferenceParticipant>[1];
    }) => messagingApi.addCallCenterConferenceParticipant(callLogId, input),
    onSuccess: () => {
      invalidateCallLists(queryClient);
    },
  });
};

export const useHoldCallCenterConferenceParticipants = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      callLogId,
      input,
    }: {
      callLogId: string;
      input: Parameters<typeof messagingApi.holdCallCenterConferenceParticipants>[1];
    }) => messagingApi.holdCallCenterConferenceParticipants(callLogId, input),
    onSuccess: () => {
      invalidateCallLists(queryClient);
    },
  });
};

export const useEndCallCenterCall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (callLogId: string) => messagingApi.endCallCenterCall(callLogId),
    onSuccess: () => {
      invalidateCallLists(queryClient);
    },
  });
};
